/**
 * Composition root — wire ports to adapters and inject into use cases.
 * Grows as migration waves complete.
 */
import { SimulationEngine } from '../../domain/services/SimulationEngine';
import { OrgImpactApplier } from '../../domain/services/OrgImpactApplier';
import { ProcessGameTickOrchestrator } from '../../application/use-cases/ProcessGameTickOrchestrator';
import { RunGameTickUseCase } from '../../application/use-cases/RunGameTickUseCase';
import { EnforcePoliciesUseCase } from '../../application/use-cases/EnforcePoliciesUseCase';
import { CreatePolicyUseCase } from '../../application/use-cases/CreatePolicyUseCase';
import { ApprovePolicyUseCase } from '../../application/use-cases/ApprovePolicyUseCase';
import { PolicyManagerAdapter } from '../../adapters/policy/PolicyManagerAdapter';
import { PolicyEnforcementAdapter } from '../../adapters/policy/PolicyEnforcementAdapter';
import { GeminiAiAdvisorAdapter } from '../../adapters/ai/GeminiAiAdvisorAdapter';
import { SaveGameUseCase } from '../../application/use-cases/SaveGameUseCase';
import { LoadGameUseCase } from '../../application/use-cases/LoadGameUseCase';
import { StartScenarioUseCase } from '../../application/use-cases/StartScenarioUseCase';
import { EvaluateScenarioObjectiveUseCase } from '../../application/use-cases/EvaluateScenarioObjectiveUseCase';
import { EvaluateMilestoneUseCase } from '../../application/use-cases/EvaluateMilestoneUseCase';
import { UpdateOrgSimulationUseCase } from '../../application/use-cases/UpdateOrgSimulationUseCase';
import { PlaceBuildingUseCase } from '../../application/use-cases/PlaceBuildingUseCase';
import { CalculateConnectivityUseCase } from '../../application/use-cases/CalculateConnectivityUseCase';
import { EvaluateEnhancedObjectivesUseCase } from '../../application/use-cases/EvaluateEnhancedObjectivesUseCase';
import { EnhancedObjectivesAdapter } from '../../adapters/enhancedObjectives/EnhancedObjectivesAdapter';
import { OrgManagementAdapter } from '../../adapters/org/OrgManagementAdapter';
import { AdvanceResearchUseCase } from '../../application/use-cases/AdvanceResearchUseCase';
import { LaunchProductUseCase } from '../../application/use-cases/LaunchProductUseCase';
import { ProcessLoanUseCase } from '../../application/use-cases/ProcessLoanUseCase';
import { StartResearchProjectUseCase } from '../../application/use-cases/StartResearchProjectUseCase';
import { PitchInvestorsUseCase } from '../../application/use-cases/PitchInvestorsUseCase';
import { ExecuteSocActionUseCase } from '../../application/use-cases/ExecuteSocActionUseCase';
import { TriggerScriptedEventUseCase } from '../../application/use-cases/TriggerScriptedEventUseCase';
import { RequestAiCoachingUseCase } from '../../application/use-cases/RequestAiCoachingUseCase';
import { HireEmployeeUseCase } from '../../application/use-cases/HireEmployeeUseCase';
import { OnboardVendorUseCase } from '../../application/use-cases/OnboardVendorUseCase';
import { ScheduleAuditUseCase } from '../../application/use-cases/ScheduleAuditUseCase';
import { SubmitPolicyForReviewUseCase } from '../../application/use-cases/SubmitPolicyForReviewUseCase';
import { ImplementPolicyUseCase } from '../../application/use-cases/ImplementPolicyUseCase';
import { ActivatePolicyUseCase } from '../../application/use-cases/ActivatePolicyUseCase';
import { ActivatePolicyChallengeUseCase } from '../../application/use-cases/ActivatePolicyChallengeUseCase';
import { TriggerPolicyAuditUseCase } from '../../application/use-cases/TriggerPolicyAuditUseCase';
import { CheckZoneViolationsUseCase } from '../../application/use-cases/CheckZoneViolationsUseCase';
import { EvaluateThreatUseCase } from '../../application/use-cases/EvaluateThreatUseCase';
import { TriggerRandomEventUseCase } from '../../application/use-cases/TriggerRandomEventUseCase';
import { SwitchUndergroundLayerUseCase } from '../../application/use-cases/SwitchUndergroundLayerUseCase';
import { GenerateAiNewsUseCase } from '../../application/use-cases/GenerateAiNewsUseCase';
import { PlayAudioUseCase } from '../../application/use-cases/PlayAudioUseCase';
import { LoadTooltipContentUseCase } from '../../application/use-cases/LoadTooltipContentUseCase';
import { ZoneComplianceAdapter } from '../../adapters/compliance/ZoneComplianceAdapter';
import { LayerManagerAdapter } from '../../adapters/layer/LayerManagerAdapter';
import { AudioServiceAdapter } from '../../adapters/audio/AudioServiceAdapter';
import { TooltipContentAdapter } from '../../adapters/tooltip/TooltipContentAdapter';
import type { IGameRepository } from '../../domain/ports/IGameRepository';
import { LocalStorageGameRepository } from '../../adapters/persistence/LocalStorageGameRepository';
import { HttpGameRepository } from '../../adapters/persistence/HttpGameRepository';
import { CompositeGameRepository } from '../../adapters/persistence/CompositeGameRepository';
import { HttpProgressRepository } from '../../adapters/persistence/HttpProgressRepository';
import { LocalProgressRepository } from '../../adapters/persistence/LocalProgressRepository';
import { ConsoleAppLogger } from '../../adapters/logging/ConsoleAppLogger';
import { architectureFeatures } from '../config/features';
import { getPlatformApiUrl } from '../config/platform';
import { industryCatalog } from '../../adapters/industry/StaticIndustryCatalog';
import { ValidateIndustryCatalogUseCase } from '../../application/use-cases/ValidateIndustryCatalogUseCase';
import { GetIndustryContentUseCase } from '../../application/use-cases/GetIndustryContentUseCase';


const simulation = new SimulationEngine();
const orgImpactApplier = new OrgImpactApplier();
const orgManagementPort = new OrgManagementAdapter();
const orgDataProvider = {
  getOrgTickData: () => ({
    employees: orgManagementPort.getEmployees(),
    vendors: orgManagementPort.getVendors(),
    audits: orgManagementPort.getAudits(),
    checklists: orgManagementPort.getChecklists(),
    workflows: orgManagementPort.getWorkflows(),
  }),
};
const logger = new ConsoleAppLogger();

export const calculateConnectivityUseCase = new CalculateConnectivityUseCase({
  simulation,
  logger,
});

const processGameTickOrchestrator = new ProcessGameTickOrchestrator({
  simulation,
  calculateConnectivity: calculateConnectivityUseCase,
  orgImpact: orgImpactApplier,
  orgDataProvider,
});

/** Local-only, platform-only, or dual-write composite based on feature flag */
export function getGameRepository(): IGameRepository {
  const local = new LocalStorageGameRepository();
  if (architectureFeatures.usePlatformPersistence) {
    return new CompositeGameRepository(local, new HttpGameRepository());
  }
  return local;
}

const gameRepository = getGameRepository();

const progressRepository = architectureFeatures.usePlatformPersistence
  ? new HttpProgressRepository(getPlatformApiUrl())
  : new LocalProgressRepository();

export const runGameTickUseCase = new RunGameTickUseCase({
  orchestrator: processGameTickOrchestrator,
  logger,
});

const policyManagerPort = new PolicyManagerAdapter();
const policyEnforcementPort = new PolicyEnforcementAdapter();
const aiAdvisorPort = new GeminiAiAdvisorAdapter();
const zoneCompliancePort = new ZoneComplianceAdapter();
const layerManagerPort = new LayerManagerAdapter();
const audioPort = new AudioServiceAdapter();
const tooltipContentPort = new TooltipContentAdapter();

export const enforcePoliciesUseCase = new EnforcePoliciesUseCase({
  policyEnforcement: policyEnforcementPort,
  policyManager: policyManagerPort,
  logger,
});

export const createPolicyUseCase = new CreatePolicyUseCase({
  policyManager: policyManagerPort,
  logger,
});

export const approvePolicyUseCase = new ApprovePolicyUseCase({
  policyManager: policyManagerPort,
  logger,
});

export const saveGameUseCase = new SaveGameUseCase({
  repository: gameRepository,
  logger,
});

export const loadGameUseCase = new LoadGameUseCase({
  repository: gameRepository,
  logger,
});

export const startScenarioUseCase = new StartScenarioUseCase({ logger, catalog: industryCatalog });

export const updateOrgSimulationUseCase = new UpdateOrgSimulationUseCase({
  orgManagement: orgManagementPort,
  logger,
});

export const evaluateScenarioObjectiveUseCase = new EvaluateScenarioObjectiveUseCase({
  simulation,
  logger,
});

export const evaluateMilestoneUseCase = new EvaluateMilestoneUseCase({ logger });

const enhancedObjectivesEvaluator = new EnhancedObjectivesAdapter();

export const evaluateEnhancedObjectivesUseCase = new EvaluateEnhancedObjectivesUseCase({
  evaluator: enhancedObjectivesEvaluator,
  logger,
});

export const placeBuildingUseCase = new PlaceBuildingUseCase({
  simulation,
  calculateConnectivity: calculateConnectivityUseCase,
  logger,
});

export const advanceResearchUseCase = new AdvanceResearchUseCase({ logger });

export const executeSocActionUseCase = new ExecuteSocActionUseCase({ logger });

export const launchProductUseCase = new LaunchProductUseCase({ logger });

export const processLoanUseCase = new ProcessLoanUseCase({ logger });

export const startResearchProjectUseCase = new StartResearchProjectUseCase({ logger });

export const pitchInvestorsUseCase = new PitchInvestorsUseCase({ logger });

export const triggerScriptedEventUseCase = new TriggerScriptedEventUseCase({ logger });

export const requestAiCoachingUseCase = new RequestAiCoachingUseCase({
  aiAdvisor: aiAdvisorPort,
  logger,
});

export const hireEmployeeUseCase = new HireEmployeeUseCase({
  orgManagement: orgManagementPort,
  logger,
});

export const onboardVendorUseCase = new OnboardVendorUseCase({
  orgManagement: orgManagementPort,
  logger,
});

export const scheduleAuditUseCase = new ScheduleAuditUseCase({
  orgManagement: orgManagementPort,
  logger,
});

export const submitPolicyForReviewUseCase = new SubmitPolicyForReviewUseCase({
  policyManager: policyManagerPort,
  logger,
});

export const implementPolicyUseCase = new ImplementPolicyUseCase({
  policyManager: policyManagerPort,
  logger,
});

export const activatePolicyUseCase = new ActivatePolicyUseCase({
  policyManager: policyManagerPort,
  logger,
});

export const activatePolicyChallengeUseCase = new ActivatePolicyChallengeUseCase({
  policyManager: policyManagerPort,
  logger,
});

export const triggerPolicyAuditUseCase = new TriggerPolicyAuditUseCase({
  policyEnforcement: policyEnforcementPort,
  logger,
});

export const checkZoneViolationsUseCase = new CheckZoneViolationsUseCase({
  zoneChecker: zoneCompliancePort,
  logger,
});

export const evaluateThreatUseCase = new EvaluateThreatUseCase({ logger });

export const triggerRandomEventUseCase = new TriggerRandomEventUseCase({ logger });

export const switchUndergroundLayerUseCase = new SwitchUndergroundLayerUseCase({
  layerManager: layerManagerPort,
  logger,
});

export const generateAiNewsUseCase = new GenerateAiNewsUseCase({
  aiAdvisor: aiAdvisorPort,
  logger,
});

export const playAudioUseCase = new PlayAudioUseCase({
  audio: audioPort,
  logger,
});

export const loadTooltipContentUseCase = new LoadTooltipContentUseCase({
  tooltipContent: tooltipContentPort,
  logger,
});

export const validateIndustryCatalogUseCase = new ValidateIndustryCatalogUseCase({
  catalog: industryCatalog,
  logger,
});

export const getIndustryContentUseCase = new GetIndustryContentUseCase({
  catalog: industryCatalog,
  logger,
});

export { gameRepository, progressRepository, industryCatalog };